import { useState, useTransition } from "react";
import { toast } from "sonner";
import { Icone } from "@/components/Icone";
import { Aviso, Botao, Cartao } from "@/components/casca/formulario";
import { AREAS, areasDaPessoa, type AreaKey, type Papel } from "@/lib/permissoes";
import type { Convite, Membro } from "@/lib/telas/equipe";
import { alterarPapelAction, cancelarConviteAction, convidarAction, definirAcessoAction, removerMembroAction } from "@/web/src/acoes-config";

const ENTRADA =
  "w-full rounded-[10px] border border-[var(--color-borda)] bg-[var(--color-painel)] px-3 py-2 text-sm text-[var(--color-tinta)] outline-none transition-colors placeholder:text-[var(--color-fraco)] focus:border-[var(--color-marca)] focus:bg-[var(--color-cartao)]";

export function Equipe({
  membros,
  convites,
  lugares,
  ehAdmin,
}: {
  membros: Membro[];
  convites: Convite[];
  lugares: { usados: number; teto: number | null };
  ehAdmin: boolean;
}) {
  const [email, setEmail] = useState("");
  const [papel, setPapel] = useState<Papel>("membro");
  const [pendente, iniciar] = useTransition();
  const lotado = lugares.teto !== null && lugares.usados >= lugares.teto;

  return (
    <div className="space-y-6">
      {ehAdmin && (
        <Cartao>
          <p className="rotulo mb-1.5">Convidar</p>
          <h2 className="text-base font-bold text-[var(--color-tinta)]">Chamar alguém para a clínica</h2>
          <p className="mt-1 mb-3 text-[13.5px] text-[var(--color-suave)]">
            A pessoa recebe um e-mail e entra com a própria conta.
            {lugares.teto ? ` ${lugares.usados} de ${lugares.teto} lugares usados, contando convites pendentes.` : ""}
          </p>
          <form
            className="flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              iniciar(async () => {
                const r = await convidarAction(email, papel);
                if (!r.ok) toast.error(r.erro);
                else {
                  toast.success("Convite enviado.");
                  setEmail("");
                }
              });
            }}
          >
            <input type="email" placeholder="pessoa@clinica.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={pendente} className={`${ENTRADA} flex-1`} />
            <select value={papel} onChange={(e) => setPapel(e.target.value as Papel)} disabled={pendente} className={`${ENTRADA} sm:w-52`}>
              <option value="membro">Equipe (recepção)</option>
              <option value="admin">Administrador</option>
            </select>
            <button type="submit" disabled={pendente || !email.includes("@") || lotado} className="flex shrink-0 items-center justify-center gap-1.5 h-10 rounded-full bg-[var(--color-marca)] px-4 text-[13px] font-semibold text-[var(--color-sobre-marca)] transition-[filter] hover:brightness-110 disabled:opacity-40">
              <Icone nome="email" className="h-3.5 w-3.5" />
              Convidar
            </button>
          </form>
          {lotado && <p className="mt-2 text-xs text-[var(--color-atencao)]">Os lugares desta clínica estão ocupados. Remova alguém ou cancele um convite.</p>}
        </Cartao>
      )}

      <Cartao>
        <p className="rotulo mb-1.5">Membros · {membros.length}</p>
        <p className="mb-3 text-[13.5px] text-[var(--color-suave)]">
          Administrador vê tudo. Equipe vê o dia a dia — e o admin pode liberar mais para uma pessoa específica.
        </p>
        <div className="divide-y divide-[var(--color-borda)]">
          {membros.map((m) => (
            <LinhaMembro key={m.userId} m={m} ehAdmin={ehAdmin} />
          ))}
        </div>
      </Cartao>

      {convites.length > 0 && (
        <Cartao>
          <p className="rotulo mb-2">Convites pendentes</p>
          <div className="divide-y divide-[var(--color-borda)]">
            {convites.map((c) => (
              <div key={c.id} className="flex items-center gap-3 py-2">
                <Icone nome="email" className="h-4 w-4 shrink-0 text-[var(--color-fraco)]" />
                <span className="flex-1 truncate text-sm">{c.email}</span>
                <span className="rounded-full bg-[var(--color-painel)] px-2.5 py-0.5 text-xs font-semibold text-[var(--color-suave)] shadow-[inset_0_0_0_1px_var(--color-borda)]">
                  {c.papel === "admin" ? "Administrador" : "Equipe"}
                </span>
                <span className="text-xs text-[var(--color-fraco)]">{new Date(c.criadoISO).toLocaleDateString("pt-BR")}</span>
                {ehAdmin && (
                  <button
                    type="button"
                    aria-label="Cancelar convite"
                    onClick={() =>
                      iniciar(async () => {
                        const r = await cancelarConviteAction(c.id);
                        if (!r.ok) toast.error(r.erro);
                        else toast.success("Convite cancelado.");
                      })
                    }
                    className="rounded p-1 text-[var(--color-fraco)] hover:bg-[var(--color-alerta)]/10 hover:text-[var(--color-alerta)]"
                  >
                    <Icone nome="fechar" className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </Cartao>
      )}

      <Aviso tom="info">
        Tirar alguém daqui remove o acesso a ESTA clínica. A conta continua existindo — e, se a pessoa for de
        outra clínica também, ela continua entrando lá.
      </Aviso>
    </div>
  );
}

function LinhaMembro({ m, ehAdmin }: { m: Membro; ehAdmin: boolean }) {
  const [abertoAcesso, setAbertoAcesso] = useState(false);
  const padrao = areasDaPessoa("membro", null);
  const [sel, setSel] = useState<Set<AreaKey>>(new Set(m.areasProprias ?? padrao));
  const [pendente, iniciar] = useTransition();

  const salvarAcesso = (areas: AreaKey[] | null) =>
    iniciar(async () => {
      const r = await definirAcessoAction(m.userId, areas);
      if (!r.ok) toast.error(r.erro);
      else {
        toast.success(areas ? "Acesso salvo." : "De volta ao padrão.");
        if (!areas) setSel(new Set(padrao));
        setAbertoAcesso(false);
      }
    });

  return (
    <div className="py-3">
      <div className="flex flex-wrap items-center gap-3">
        {m.avatar ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={m.avatar} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-realce)] text-xs font-bold text-[var(--color-marca-forte)]">
            {m.nome.slice(0, 1).toUpperCase()}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-[var(--color-tinta)]">
            {m.nome}
            {m.ehVoce && <span className="ml-1 text-xs text-[var(--color-fraco)]">(você)</span>}
          </p>
          <p className="truncate text-xs text-[var(--color-suave)]">{m.email}</p>
        </div>

        {ehAdmin && !m.ehVoce ? (
          <select
            value={m.papel}
            disabled={pendente}
            onChange={(e) =>
              iniciar(async () => {
                const r = await alterarPapelAction(m.userId, e.target.value as Papel);
                if (!r.ok) toast.error(r.erro);
              })
            }
            className="h-8 rounded-full border-0 bg-[var(--color-cartao)] px-3 text-[13px] font-semibold text-[var(--color-tinta)] shadow-botao outline-none"
          >
            <option value="membro">Equipe</option>
            <option value="admin">Administrador</option>
          </select>
        ) : (
          <span className="rounded-full bg-[var(--color-painel)] px-2.5 py-0.5 text-xs font-semibold text-[var(--color-suave)] shadow-[inset_0_0_0_1px_var(--color-borda)]">
            {m.papel === "admin" ? "Administrador" : "Equipe"}
          </span>
        )}

        {m.papel === "admin" ? (
          <span className="text-xs text-[var(--color-fraco)]">acesso total</span>
        ) : ehAdmin && !m.ehVoce ? (
          <button
            type="button"
            onClick={() => setAbertoAcesso((v) => !v)}
            className={
              "flex h-8 items-center gap-1.5 rounded-full px-3 text-[13px] font-semibold transition-colors " +
              (m.areasProprias ? "bg-[var(--color-realce)] text-[var(--color-marca-forte)]" : "text-[var(--color-suave)] hover:bg-[var(--color-painel)]")
            }
          >
            <Icone nome="ajustes" className="h-3.5 w-3.5" />
            {m.areasProprias ? `${m.areasProprias.length} áreas` : "padrão"}
          </button>
        ) : null}

        {ehAdmin && !m.ehVoce && (
          <button
            type="button"
            aria-label="Remover da clínica"
            disabled={pendente}
            onClick={() => {
              if (!confirm(`Tirar ${m.nome} da clínica? A conta continua existindo; só perde o acesso a esta clínica.`)) return;
              iniciar(async () => {
                const r = await removerMembroAction(m.userId);
                if (!r.ok) toast.error(r.erro);
                else toast.success("Removido.");
              });
            }}
            className="rounded p-1 text-[var(--color-fraco)] hover:bg-[var(--color-alerta)]/10 hover:text-[var(--color-alerta)]"
          >
            <Icone nome="lixeira" className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {abertoAcesso && (
        <div className="mt-3 rounded-xl bg-[var(--color-painel)] p-3">
          <p className="mb-2 text-[13.5px] text-[var(--color-suave)]">
            O que <strong className="text-[var(--color-tinta)]">{m.nome}</strong> enxerga no menu
          </p>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {AREAS.map((a) => (
              <label key={a.key} className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={sel.has(a.key)}
                  disabled={pendente}
                  onChange={(e) =>
                    setSel((s) => {
                      const n = new Set(s);
                      if (e.target.checked) n.add(a.key);
                      else n.delete(a.key);
                      return n;
                    })
                  }
                  className="rounded border-[var(--color-borda)] text-[var(--color-marca)] focus:ring-0"
                />
                {a.rotulo}
              </label>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <Botao icone="check" disabled={pendente} onClick={() => salvarAcesso([...sel])}>
              Salvar acesso
            </Botao>
            {m.areasProprias && (
              <Botao variante="discreto" icone="reabrir" disabled={pendente} onClick={() => salvarAcesso(null)}>
                Restaurar padrão
              </Botao>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
